import { BasePopup } from 'app/components/BasePopup';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './Filters.less';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { BaseButton } from 'app/components/buttons/BaseButton';
import clsx from 'clsx';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { Accordion } from 'app/components/accordions';
import { getFilterSectionSubtitles } from './utils';
import { AccordionGroup } from 'app/components/accordions';

interface Props {
  anchorEl?: HTMLElement;
  onClose: () => void;
}

// TODO Refactor the UI and general filtering functionality when API and business requirements are ready.
// Transfer responsibility to backend, have a single point for processing information
// (rtk-query, redux-store, context api), instead of doing it inside components.
export default function Filters({ anchorEl, onClose }: Props) {
  const { equipmentListData: data, setEquipmentListData: setData } =
    React.useContext(EquipmentListContext);
  const { t } = useTranslation();
  const isSubscriptionsFeatureEnabled = useFlag(FeatureName.SUBSCRIPTIONS);

  const subtitles = useMemo(
    () => getFilterSectionSubtitles(data.filtersList, t),
    [data.filtersList],
  );

  const selectedFilterItems = useMemo(
    () => ({
      activeFaultCodes:
        data.filtersList?.includes('active_fault_codes') ?? false,
      machineRunning: data.filtersList?.includes('machine_running') ?? false,
      machineInTransport:
        data.filtersList?.includes('machine_in_transport') ?? false,
      restartInhibited:
        data.filtersList?.includes('restart_inhibited') ?? false,
      basicTelematicsPlan:
        data.filtersList?.includes('basic_telematics_plan') ?? false,
      premiumTelematicsPlan:
        data.filtersList?.includes('premium_telematics_plan') ?? false,
      expiringSoon: data.filtersList?.includes('expiring_soon') ?? false,
    }),
    [data.filtersList],
  );

  const handleCheck = e => {
    const { id, checked } = e.target;
    const updatedFiltersList = checked
      ? [...(data.filtersList || []), id]
      : (data.filtersList || []).filter(item => item !== id);

    setData({
      ...data,
      filtersList: updatedFiltersList,
    });
  };

  return (
    <BasePopup
      className="filters-popup"
      anchor={anchorEl}
      ignoreAnchorOnMobile
      onClose={onClose}
    >
      <div className="filters-popup__header">
        <h3 className="filters-popup__title">{t('equipment_filters.title')}</h3>

        {data.filtersList && data.filtersList.length > 0 && (
          <BaseButton
            className="filters-popup__clear-button"
            onClick={() =>
              setData({
                ...data,
                filtersList: [],
              })
            }
          >
            {t('clear_filters')}
          </BaseButton>
        )}
      </div>

      <AccordionGroup>
        <Accordion
          title={t('equipment_filters.sections.machine_info.title')}
          subtitle={subtitles.machineInfo}
          className="filters-popup__options"
        >
          <div className="filters-popup__option">
            <label
              htmlFor="active_fault_codes"
              className="filters-popup__label"
            >
              {t('equipment_filters.sections.machine_info.active_fault_codes')}
            </label>
            <input
              id="active_fault_codes"
              type="checkbox"
              className="filters-popup__checkbox"
              checked={selectedFilterItems.activeFaultCodes}
              onChange={handleCheck}
            />
          </div>

          <div className="filters-popup__option">
            <label htmlFor="machine_running" className="filters-popup__label">
              {t('equipment_filters.sections.machine_info.machine_running')}
            </label>
            <input
              id="machine_running"
              type="checkbox"
              className="filters-popup__checkbox"
              checked={selectedFilterItems.machineRunning}
              onChange={handleCheck}
            />
          </div>

          <div className="filters-popup__option">
            <label
              htmlFor="machine_in_transport"
              className={clsx('filters-popup__label', {
                'filters-popup__label_active':
                  selectedFilterItems.machineInTransport,
              })}
            >
              {t(
                'equipment_filters.sections.machine_info.machine_in_transport',
              )}
            </label>
            <input
              id="machine_in_transport"
              type="checkbox"
              className="filters-popup__checkbox"
              checked={selectedFilterItems.machineInTransport}
              onChange={handleCheck}
            />
          </div>

          <div className="filters-popup__option">
            <label htmlFor="restart_inhibited" className="filters-popup__label">
              {t('equipment_filters.sections.machine_info.restart_inhibited')}
            </label>
            <input
              id="restart_inhibited"
              type="checkbox"
              className="filters-popup__checkbox"
              checked={selectedFilterItems.restartInhibited}
              onChange={handleCheck}
            />
          </div>
        </Accordion>

        {isSubscriptionsFeatureEnabled && (
          <Accordion
            title={t(
              'equipment_filters.sections.kubota_now_subscription.title',
            )}
            subtitle={subtitles.subscriptions}
            className="filters-popup__options"
          >
            <div className="filters-popup__option">
              <label
                htmlFor="basic_telematics_plan"
                className="filters-popup__label"
              >
                {t(
                  'equipment_filters.sections.kubota_now_subscription.basic_telematics_plan',
                )}
              </label>
              <input
                id="basic_telematics_plan"
                type="checkbox"
                className="filters-popup__checkbox"
                checked={selectedFilterItems.basicTelematicsPlan}
                onChange={handleCheck}
              />
            </div>

            <div className="filters-popup__option">
              <label
                htmlFor="premium_telematics_plan"
                className="filters-popup__label"
              >
                {t(
                  'equipment_filters.sections.kubota_now_subscription.premium_telematics_plan',
                )}
              </label>

              <input
                id="premium_telematics_plan"
                type="checkbox"
                className="filters-popup__checkbox"
                checked={selectedFilterItems.premiumTelematicsPlan}
                onChange={handleCheck}
              />
            </div>

            <div className="filters-popup__option">
              <label htmlFor="expiring_soon" className="filters-popup__label">
                {t(
                  'equipment_filters.sections.kubota_now_subscription.expiring_soon',
                )}
              </label>
              <input
                id="expiring_soon"
                type="checkbox"
                className="filters-popup__checkbox"
                checked={selectedFilterItems.expiringSoon}
                onChange={handleCheck}
              />
            </div>
          </Accordion>
        )}
      </AccordionGroup>
    </BasePopup>
  );
}
